import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const stored = localStorage.getItem("cart_session_id");
    return stored || crypto.randomUUID();
  });

  useEffect(() => {
    localStorage.setItem("cart_session_id", sessionId);
  }, [sessionId]);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, selectedColor = null, instructions = "", priceOverride = null, categorySlug = null) => {
    try {
      setLoading(true);
      await axios.post(`${API}/cart/${sessionId}/add`, {
        product_id: productId,
        quantity,
        selected_color: selectedColor,
        instructions: instructions,
        price_override: priceOverride
      });
      // Store the last added product's category for "Continue Shopping" link
      if (categorySlug) {
        localStorage.setItem("last_shopping_category", categorySlug);
      }
      await fetchCart();
      return { success: true };
    } catch (error) {
      console.error("Error adding to cart:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      setLoading(true);
      await axios.post(`${API}/cart/${sessionId}/update`, {
        product_id: productId,
        quantity
      });
      await fetchCart();
    } catch (error) {
      console.error("Error updating cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      setLoading(true);
      await axios.delete(`${API}/cart/${sessionId}/item/${productId}`);
      await fetchCart();
    } catch (error) {
      console.error("Error removing from cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API}/cart/${sessionId}`);
      await fetchCart();
    } catch (error) {
      console.error("Error clearing cart:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        sessionId,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        fetchCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
